/**
 * Per-(doc, section, contentHash, model-version) extraction cache.
 *
 * KSD6 — Within-rebuild memoisation of `extractFromDocument`. For a
 * Redhead-scale site (~80 user docs) the SED rebuild called
 * `extractFromDocument` once per applicable section per doc — same
 * Gemini call, same prompt, same answer, repeated 5x per doc per
 * rebuild. That added up to ~400 Gemini round-trips per rebuild and
 * was the primary culprit behind LB1's 19-min stall.
 *
 * This module wraps `extractFromDocument` in a two-layer cache:
 *
 *   1. In-memory `Map<key, ExtractFromDocumentResult>` — survives the
 *      bootstrap pass. Key includes the doc's `contentHash` so the
 *      same doc re-uploaded with different bytes gets a fresh
 *      extraction (no stale-data risk).
 *   2. Firestore-backed persistent layer at
 *      `sites/{siteId}/extractionCache/{cacheKey}` — survives the
 *      in-memory reset between rebuilds. The first rebuild populates
 *      it; subsequent rebuilds on the same docs read from Firestore
 *      and skip Gemini entirely.
 *
 * Invalidation:
 *   - `doc.contentHash` changes (re-upload) → new cache key → fresh call.
 *   - `MODEL_VERSION` bumps → new cache key → fresh call (covers prompt /
 *     schema / temperature changes).
 *   - Manual invalidation: callers may pass `{ bypassCache: true }` —
 *     used by the KSD5 per-doc Re-extract button so admins can force a
 *     fresh extraction without bumping the model version.
 *
 * Public symbol:
 *   - `memoisedExtractFromDocument({ doc, section, siteId, bypassCache, runner })`
 *     A drop-in for `extractFromDocument` that participates in the cache.
 *     The underlying `extractFromDocument` is invoked only on cache miss.
 *
 * Engineering invariants:
 *   - The cache MUST NOT serve stale results. A doc with a `contentHash`
 *     that doesn't match the cached entry MUST miss.
 *   - The cache MUST be process-local for the in-memory layer (no
 *     cross-tab leaks via global mutation outside this module).
 *   - The Firestore layer is best-effort. A write or read failure MUST
 *     fall through to the wrapped function — never abort the bootstrap.
 *   - Failed extraction calls are NOT cached. A completed requirement
 *     response with no eligible fields has an explicit terminal receipt,
 *     allowing a bounded sweep to advance beyond non-matching documents.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { SedSectionId } from '../../types/siteEvidence';
import type {
  ExtractFromDocumentResult,
  ExtractFromDocumentOutcome,
  ExtractRequirementFieldsInput,
  ExtractRequirementFieldsResult,
} from './extractFromDocument';
import {
  extractFromDocument as _extractFromDocument,
  extractFromDocumentDetailed as _extractFromDocumentDetailed,
  extractRequirementFieldsFromDocument as _extractRequirementFields,
  requirementExtractionEligible,
  isExtractionRejection,
} from './extractFromDocument';
import { requirementSchemaFingerprint } from '../../lib/coverage/requirementDocExtractSchemas';
import type { runGeminiJSON } from '../geminiClient';
// CW-2 — `stripUndefined` is imported statically (not lazily inside the
// write helper) so the enqueue path stays fully synchronous. The shared
// helper is the same surface used pre-CW-2 inside the
// dynamic-import block — there is no behavioural change.
import { stripUndefined as _sharedStripUndefined } from '../../lib/firestore/stripUndefined';
import { throwIfAborted } from '../../lib/abortSignal';
import { documentBodyText } from '../../lib/documentBody';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';
import { sanitizeDocumentSectionFields } from '../../lib/sed/documentExtractSanity';
import { filterRequirementFieldsBySource } from '../../lib/sed/requirementSourceEvidence';

/**
 * Prompt/schema/model version stamp. Bump when ANY of the following
 * change in `extractFromDocument`:
 *   - the prompt body (contents string)
 *   - the section schemas (SECTION_SCHEMAS)
 *   - the Gemini model identifier
 * A bump invalidates every prior cache entry on the next read — the
 * stale entry simply misses (key mismatch) so memory + Firestore get
 * lazily-overwritten without an explicit purge step.
 *
 * REF-LINEAGE REF-1 — bumped from 'v1' → 'v2' to invalidate cache
 * entries written before the extractor was extended with
 * `pageNumber` + `sectionHeading` + `excerptPreview` lineage fields.
 *
 * DAT-FOLLOWUP-1 — bumped from 'v2' → 'v3' to invalidate cache entries
 * written before the locus-binding instruction landed in the prompt
 * and the post-hoc cross-state guard scrubbed cross-attributed
 * `regulatory.state` values. Per the comment block at lines 24-46,
 * the bump alone is the invalidation story — every prior memory +
 * Firestore entry key-mismatches on the next read and is
 * lazy-overwritten with the new shape. No migration script.
 *
 * EX-2 — bumped from 'v3' → 'v4' to invalidate cache entries written
 * before the DOC-channel range/unit/plausibility validation
 * (`validateSectionData`) + boolean negation flip (`applyDocBooleanNegation`)
 * landed in `extractFromDocument`. The memoised result is cached
 * VERBATIM, so without this bump a hot/persisted pre-validation entry
 * would keep serving the wrong (out-of-range / inverted) values the
 * new guards reject. The bump alone invalidates them — stale keys miss
 * and are lazy-overwritten with the validated shape. No migration script.
 *
 * EX-4 — bumped from 'v4' → 'v5' to invalidate cache entries written
 * before the DOC-channel locus-keyword / source-span verification landed
 * (`validateSectionData(section, data, locusSpan)`). The locus check
 * rejects IN-RANGE wrong values (load-factor 5.5↔83.3, acreage 332↔800)
 * that the v4 range guard let through, so a hot/persisted v4 entry would
 * keep serving those wrong values without the bump. Same invalidation
 * story — stale keys miss and lazy-overwrite. No migration script.
 *
 * SED D0.3 — bumped from 'v5' → 'v6' to invalidate cache entries written
 * before the extraction-recall change altered the extractor's OUTPUT
 * SEMANTICS: the per-section `required` arrays were dropped (so a
 * PARTIAL payload that v5 coerced/nulled now lands its subset), and the
 * extraction-attempt floor dropped 0.6 → 0.4 (so docs in the [0.4, 0.6)
 * band are extracted for the first time). A hot/persisted v5 entry would
 * keep serving the old coerced/empty result; the bump forces a one-time
 * full miss so the new partial-extraction shape is recomputed. Stale
 * keys miss and lazy-overwrite — no migration script.
 *
 * H-4 (passage-offset citation deep-links) — bumped from 'v6' → 'v7' to
 * invalidate cache entries written before the success result gained the
 * `sourceOffset` field (the verbatim-excerpt character span used for the
 * passage-level citation deep-link). The memoised result is cached
 * VERBATIM, so without this bump a hot/persisted v6 entry would keep
 * serving a result with no `sourceOffset` and the deep-link would never
 * reach offset precision (it would fall back to page → filename). The
 * bump alone is the invalidation story — stale keys miss and
 * lazy-overwrite with the offset-bearing shape. No migration script. This
 * is purely additive: an offset-less re-computed result still renders +
 * links exactly as a v6 entry did.
 */
 /**
 * Doc-extraction program (D6/D10) — bumped from 'v7' → 'v8' to invalidate
 * cache entries written before (a) the connectivity schema gained
 * `nearestFiberKm` (a v7 hit would keep serving a payload that can never
 * carry the field), and (b) the requirement-channel sanitizer gained
 * schema-driven enum canonicalization + date validation, which changes
 * what a cached channel-A section payload's downstream consumers assume
 * about casing. Stale keys miss and lazy-overwrite — no migration script.
 *
 * QP-1 (quality pass) — bumped 'v8' → 'v9': the section-extraction prompt
 * window widened 8,000 → 60,000 chars (SECTION_EXTRACTION_TEXT_WINDOW),
 * plus QP-3 added plausibility ranges for 13 previously-unguarded numeric
 * keys. A v8 hit would keep serving a payload extracted from the first
 * 8k chars (missing everything past the cover pages) and unvalidated
 * values. Stale keys miss and lazy-overwrite — no migration script.
 */
// v10 (P1.4, 2026-09-07) — extraction reads the WHOLE document body (P1.2)
// with a windowed second pass; v9 entries were extracted from the 10k preview.
// v11 — P4: every extraction now returns stated attribution (who said it, for which project).
// v12 — preserve the 0.6 visual-screening cap through typed extraction.
export const EXTRACTION_MODEL_VERSION = 'v12';

/** In-memory cache. Process-local; cleared by `clearExtractionCache()` (tests). */
const memoryCache = new Map<string, ExtractFromDocumentResult>();
/** Cache-hit/miss counters for telemetry assertions in tests. */
const stats = { hits: 0, misses: 0, firestoreHits: 0, firestoreMisses: 0 };

export interface MemoisedExtractInput {
  /** Attempt-owned cancellation/watchdog signal. */
  abortSignal?: AbortSignal;
  doc: SiteDocument;
  section: SedSectionId;
  /** Optional — when present, enables the Firestore-persistent layer. */
  siteId?: string;
  /** Forces a fresh extraction (used by KSD5 Re-extract). */
  bypassCache?: boolean;
  /** Override for tests; passed through to `extractFromDocument`. */
  runner?: typeof runGeminiJSON;
  /**
   * DAT-FOLLOWUP-1 — site-state prior forwarded to
   * `extractFromDocument`. Two-letter USPS code; `null`/`undefined`
   * disables the cross-state guard (legacy behaviour). Included in the
   * source identity because it changes the prompt and validation result.
   */
  siteState?: string | null;
  /** DAT-FOLLOWUP-1 — site name for locus-binding heuristics. */
  siteName?: string;
}

/** Compose the cache key — exported for tests. */
export function cacheKey(docId: string, sectionId: SedSectionId, contentHash: string): string {
  // Role/schema changes invalidate only their affected section. Preserve
  // unrelated provider/section cache identities and the wetland suffix.
  const version = sectionId === 'environmental' ? `${EXTRACTION_MODEL_VERSION}-wetland-scope-1`
    : sectionId === 'connectivity' ? `${EXTRACTION_MODEL_VERSION}-fiber-build-scope-1`
    : EXTRACTION_MODEL_VERSION;
  return `${docId}::${sectionId}::${contentHash}::${version}`;
}

/** Raw-file identity alone cannot detect improved OCR or changed source scope. */
function extractionSourceIdentity(doc: SiteDocument, scope: unknown = null): string {
  const revision = hashCanonicalJson([
    'document-extraction-source-v2', doc.storagePath, doc.updatedAt,
    doc.pdfTextReprocessedAt, doc.officeReprocessedAt,
    documentBodyText(doc), doc.fileName, doc.summary, doc.uploadedAt,
    doc.intendedRequirementIds, doc.topics, doc.topicSegments, doc.pages,
    doc.visualReadings, scope,
  ]);
  return `${doc.contentHash ?? `noh:${doc.id}`}::source-${revision}`;
}

/**
 * Reset the in-memory cache + telemetry counters. Call from tests
 * between cases so cache state from a prior test can't leak into a
 * later one. CW-2 — also clears the per-site write buffer + any
 * pending flush timers so a test that enqueues a write does not leak
 * state into the next test.
 */
export function clearExtractionCache(): void {
  memoryCache.clear();
  requirementMemoryCache.clear();
  stats.hits = 0;
  stats.misses = 0;
  stats.firestoreHits = 0;
  stats.firestoreMisses = 0;
  for (const t of flushTimers.values()) clearTimeout(t);
  flushTimers.clear();
  writeBuffers.clear();
  inflightFlushes.clear();
}

/** Snapshot the cache stats (test assertions). */
export function getExtractionCacheStats(): Readonly<typeof stats> {
  return { ...stats };
}

/* ── Firestore-persistent layer ──────────────────────────────────────
 *
 * Path: sites/{siteId}/extractionCache/{cacheKey}
 *
 * Loaded lazily so this module imports cleanly in test envs where the
 * firebase client isn't initialised. Failures fall through to the
 * wrapped function with a console.warn — never throw.
 */

async function firestoreRead(
  siteId: string,
  key: string,
): Promise<ExtractFromDocumentResult | null> {
  try {
    // BUG-3 FIX — headless Cloud Run job has no signed-in user, so a client-web
    // SDK read is rejected by Firestore rules (7 PERMISSION_DENIED). Use the
    // admin SDK (ADC, bypasses rules) when headless; browser keeps the client
    // path. Soft-fails to null either way (cache miss → recompute).
    const { isHeadlessContext, getAdminFirestoreForContext } = await import(
      '../firestoreAdminForContext'
    );
    let raw: { result?: ExtractFromDocumentResult } | undefined;
    let exists = false;
    const adminDb = isHeadlessContext() ? await getAdminFirestoreForContext() : null;
    if (adminDb) {
      const snap = await adminDb
        .doc(`sites/${siteId}/extractionCache/${encodeKey(key)}`)
        .get();
      exists = snap.exists;
      raw = exists ? (snap.data() as { result?: ExtractFromDocumentResult }) : undefined;
    } else {
      const { db } = await import('../../firebase');
      const { doc: docRef, getDoc } = await import('firebase/firestore');
      const ref = docRef(db, 'sites', siteId, 'extractionCache', encodeKey(key));
      const snap = await getDoc(ref);
      exists = snap.exists();
      raw = exists ? (snap.data() as { result?: ExtractFromDocumentResult }) : undefined;
    }
    if (!exists || !raw || !raw.result) {
      stats.firestoreMisses += 1;
      return null;
    }
    stats.firestoreHits += 1;
    return raw.result;
  } catch (e) {
    console.debug('[extractionCache] firestore read failed:', (e as Error)?.message);
    return null;
  }
}

/* ── CW-2 — WriteBatch coalescing buffer ─────────────────────────────
 *
 * Pre-CW-2 every cache miss invoked `setDoc` directly — one Firestore
 * round-trip per extracted (doc, section) pair. On a Redhead-scale
 * rebuild that's ~400 round-trips back-to-back, which is exactly what
 * blows past the web-SDK's internal write queue (CDS-4
 * `firestore.write.exhausted` events).
 *
 * Strategy: enqueue `{ ref, payload }` into a per-process buffer.
 * Flush triggers:
 *   - Hard size cap: 500 ops per WriteBatch (Firestore's enforced
 *     limit; one batch over commits the whole thing atomically).
 *   - Soft idle: a microtask-debounced flush so a burst of writes in
 *     the same event-loop turn coalesces into one batch. The debounce
 *     window is one task tick (`setTimeout(0)`); long enough to catch
 *     the synchronous run of the SED loop, short enough not to add
 *     observable latency to the happy path.
 *
 * Idempotency (HARD RULE 9):
 *   - Doc id is the encoded cache key — fully deterministic from
 *     `(docId, sectionId, contentHash, modelVersion)`. Re-enqueuing the
 *     same key replaces the prior payload in-place inside the current
 *     buffer; once flushed, a future enqueue overwrites the Firestore
 *     doc via `batch.set` (setDoc-style, no merge needed). Callers can
 *     freely retry without duplicate-stamping.
 *
 * Performance gate (HARD RULE 8):
 *   - A single write still completes in one Firestore round-trip
 *     (~30-50 ms) — the debounce only adds the macrotask tick before
 *     the commit fires. Multi-write fan-out goes from N round-trips
 *     to ⌈N/500⌉ round-trips → strictly faster.
 */

interface PendingWrite {
  /** Encoded Firestore doc id at sites/{siteId}/extractionCache/{id}. */
  docId: string;
  /** Already-scrubbed payload ready for `batch.set`. */
  payload: Record<string, unknown>;
}

/** Per-siteId in-memory write buffer. */
const writeBuffers = new Map<string, Map<string, PendingWrite>>();
/** Active flush timer per siteId; one-shot, reset on enqueue. */
const flushTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** In-flight flush promise per siteId; awaited by `flushFirestoreWriteBuffer`. */
const inflightFlushes = new Map<string, Promise<void>>();
/** Firestore hard cap. A single WriteBatch.commit() handles ≤500 ops. */
const BATCH_HARD_CAP = 500;
/** Soft debounce window — short enough to be invisible to the happy path. */
const FLUSH_DEBOUNCE_MS = 0;

/**
 * Enqueue an extraction-cache write into the per-site batch buffer.
 * Returns void (mirroring the legacy fire-and-forget contract).
 *
 * The actual Firestore commit happens asynchronously via
 * `flushBuffer(siteId)`. Callers that want to await persistence (the
 * shutdown path, tests) can call `flushFirestoreWriteBuffer(siteId)`
 * explicitly.
 */
function firestoreWrite(
  siteId: string,
  key: string,
  result: ExtractFromDocumentResult,
): void {
  // Build the payload synchronously so the timer-based flush has
  // everything it needs without re-importing per-call. `stripUndefined`
  // is the only sync dependency; the firebase imports stay lazy in
  // `flushBuffer` so this module still loads cleanly in test envs
  // without firebase initialised.
  const docId = encodeKey(key);
  // CDS-1 — scrub at the write site. `result` carries optional lineage
  // fields (pageNumber, sectionHeading, excerptPreview) that come back
  // from `extractFromDocument` as `undefined` whenever Gemini omits
  // them. Without the scrub, `batch.set` would reject the whole batch
  // (the whole point of CDS-1) and we'd silently lose ALL writes in
  // this flush — much worse than the pre-batch one-write loss.
  const payload = stripUndefinedSync({
    result,
    key,
    modelVersion: EXTRACTION_MODEL_VERSION,
    // `serverTimestamp()` is a sentinel — we have to import it from
    // firebase/firestore at flush time. Stamp the wall-clock now so
    // tests that don't initialise firebase can still read a value.
    writtenAt: { __serverTimestamp: true } as unknown as never,
  });

  let buffer = writeBuffers.get(siteId);
  if (!buffer) {
    buffer = new Map();
    writeBuffers.set(siteId, buffer);
  }
  // Idempotency: re-enqueuing the same docId replaces the prior payload
  // in this flush window.
  buffer.set(docId, { docId, payload });

  // Hard cap: at 500 ops, flush immediately so the batch commit can fire
  // without us blowing the Firestore limit on the next enqueue.
  if (buffer.size >= BATCH_HARD_CAP) {
    const existingTimer = flushTimers.get(siteId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      flushTimers.delete(siteId);
    }
    void flushBuffer(siteId);
    return;
  }

  // Soft debounce: schedule a flush at the end of this task tick. If a
  // flush is already scheduled, leave it alone (don't reset the timer
  // — that would let a stream of enqueues postpone the flush
  // indefinitely; we want bounded latency).
  if (!flushTimers.has(siteId)) {
    const t = setTimeout(() => {
      flushTimers.delete(siteId);
      void flushBuffer(siteId);
    }, FLUSH_DEBOUNCE_MS);
    flushTimers.set(siteId, t);
  }
}

/**
 * Drain the per-site write buffer through a single WriteBatch commit.
 *
 * Coalesces with any in-flight flush — concurrent callers share the
 * same promise so a `flushFirestoreWriteBuffer(siteId)` invoked while
 * a flush is already running awaits the same commit and we don't
 * double-emit the same writes.
 */
async function flushBuffer(siteId: string): Promise<void> {
  const existing = inflightFlushes.get(siteId);
  if (existing) {
    // Coalesce: the caller waits on whatever's in flight, then we
    // check the buffer again. The buffer may have grown during the
    // flush — handle that by recursing AFTER the in-flight resolves.
    await existing;
  }
  const buffer = writeBuffers.get(siteId);
  if (!buffer || buffer.size === 0) return;
  // Snapshot + clear so new enqueues during the flush land in a fresh
  // buffer (they'll be picked up by the NEXT flush, not this one).
  const drained = Array.from(buffer.values());
  writeBuffers.delete(siteId);

  const flushPromise = doCommit(siteId, drained);
  inflightFlushes.set(siteId, flushPromise);
  try {
    await flushPromise;
  } finally {
    inflightFlushes.delete(siteId);
  }
}

/**
 * Internal — perform the actual `writeBatch.commit()` for one drained
 * buffer. Splits across multiple batches if the drain somehow grew
 * past `BATCH_HARD_CAP` (defensive: enqueue should have flushed at
 * 500, but a race during the buffer snapshot is conceivable).
 */
async function doCommit(siteId: string, drained: PendingWrite[]): Promise<void> {
  try {
    // CW-3 — route the batch commit through the shared writeLimiter
    // semaphore. The commit is one Firestore round-trip (not N), so
    // it normally doesn't need throttling — but if a second site's
    // flush + a documentUpload save + a SED rebuild's
    // patchSiteDocument fan-out are all in-flight concurrently, this
    // ensures the SHARED cap holds across all four call sites.
    const { withSemaphore } = await import('../../lib/firestore/writeLimiter');

    // BUG-3 FIX — headless Cloud Run job has no signed-in user, so a client-web
    // SDK batch commit is rejected by Firestore rules (7 PERMISSION_DENIED).
    // Use the admin SDK (ADC, bypasses rules) when headless; browser keeps the
    // client writeBatch path. Both write the SAME named DB and the SAME
    // sites/{siteId}/extractionCache/{docId} path with a server timestamp.
    const { isHeadlessContext, getAdminFirestoreForContext } = await import(
      '../firestoreAdminForContext'
    );
    const adminDb = isHeadlessContext() ? await getAdminFirestoreForContext() : null;

    if (adminDb) {
      const { FieldValue } = await import('firebase-admin/firestore');
      const ts = FieldValue.serverTimestamp();
      for (let i = 0; i < drained.length; i += BATCH_HARD_CAP) {
        const slice = drained.slice(i, i + BATCH_HARD_CAP);
        await withSemaphore(async () => {
          const batch = adminDb.batch();
          for (const { docId, payload } of slice) {
            const finalPayload = { ...payload, writtenAt: ts };
            const ref = adminDb.doc(`sites/${siteId}/extractionCache/${docId}`);
            batch.set(ref, finalPayload);
          }
          await batch.commit();
        });
      }
      return;
    }

    const { db } = await import('../../firebase');
    const { doc: docRef, writeBatch, serverTimestamp } = await import('firebase/firestore');
    const ts = serverTimestamp();
    for (let i = 0; i < drained.length; i += BATCH_HARD_CAP) {
      const slice = drained.slice(i, i + BATCH_HARD_CAP);
      await withSemaphore(async () => {
        const batch = writeBatch(db);
        for (const { docId, payload } of slice) {
          // Replace the sentinel with the real `serverTimestamp()`
          // value at commit time. Stamping `payload.writtenAt = ts`
          // is safe — payload is owned by this flush and never
          // re-read.
          const finalPayload = { ...payload, writtenAt: ts };
          const ref = docRef(db, 'sites', siteId, 'extractionCache', docId);
          batch.set(ref, finalPayload);
        }
        await batch.commit();
      });
    }
  } catch (e) {
    // CDS-4 — surface a deduped audit event when the SDK's write queue
    // is exhausted. The batched commit itself is one Firestore round-
    // trip, so this is far less likely to fire post-CW-2 — but if a
    // burst of unrelated writes from elsewhere in the app trips it,
    // admins should still see the signal.
    try {
      const { isWriteExhaustionError, shouldEmitWriteExhaustion } = await import(
        '../../lib/firestore/writeExhaustionTelemetry'
      );
      if (isWriteExhaustionError(e) && shouldEmitWriteExhaustion('extractionCache', siteId)) {
        const { emitAuditEvent } = await import('../../lib/auditLogClient');
        void emitAuditEvent({
          action: 'firestore.write.exhausted',
          target: { type: 'site', id: siteId },
          metadata: {
            operation: 'extractionCache',
            batchSize: drained.length,
            errorCode: (e as { code?: unknown })?.code,
          },
        });
      }
    } catch {
      // Telemetry is best-effort. A failure here must not mask the
      // original write failure or break the bootstrap.
    }
    console.debug('[extractionCache] firestore batch commit failed:', (e as Error)?.message);
  }
}

/**
 * Public — synchronously drain + commit the buffer for a given
 * siteId. Used by the SED bootstrap finale (to make sure the last
 * burst of writes lands before the rebuild returns) and by tests.
 *
 * Returns the in-flight flush promise so callers can `await` it.
 */
export async function flushFirestoreWriteBuffer(siteId: string): Promise<void> {
  const timer = flushTimers.get(siteId);
  if (timer) {
    clearTimeout(timer);
    flushTimers.delete(siteId);
  }
  // Drain in a loop in case new writes arrive during the flush (the
  // SED finale shouldn't generate any, but be defensive).
  // Hard ceiling at 4 passes to avoid an unbounded recursion if a
  // caller is somehow enqueuing in a tight synchronous loop.
  for (let pass = 0; pass < 4; pass++) {
    const buffer = writeBuffers.get(siteId);
    if (!buffer || buffer.size === 0) {
      // Still await any in-flight from another caller.
      const inflight = inflightFlushes.get(siteId);
      if (inflight) await inflight;
      return;
    }
    await flushBuffer(siteId);
  }
}

/**
 * Local copy of `stripUndefined` for the sync path. The exported
 * helper lives at `../../lib/firestore/stripUndefined` — it's pure,
 * so we can re-import it lazily at call time. Static import here
 * keeps the enqueue path synchronous (no awaits on the hot path).
 */
function stripUndefinedSync(value: Record<string, unknown>): Record<string, unknown> {
  // The shared helper is generic; cast through unknown to keep the
  // signature honest at this call site.
  return _sharedStripUndefined(value) as Record<string, unknown>;
}

/**
 * Firestore document ids cannot contain `/`. The cache key includes
 * `::` and the doc id; neither contains `/` in practice but we encode
 * just to be safe across docs whose ids carry path-like characters.
 */
function encodeKey(key: string): string {
  return key.replace(/\//g, '_').slice(0, 1500);
}

function checkedSectionCache(result: ExtractFromDocumentResult, doc: SiteDocument, section: SedSectionId): ExtractFromDocumentResult | null {
  const data = structuredClone(result.data);
  sanitizeDocumentSectionFields(section, data, documentBodyText(doc));
  return Object.keys(data).length > 0 ? { ...result, data } : null;
}

function checkedRequirementCache(result: ExtractRequirementFieldsResult, doc: SiteDocument, requirementId: string): MemoisedRequirementExtractOutcome {
  const fields = { ...result.fields };
  filterRequirementFieldsBySource(requirementId, fields, documentBodyText(doc));
  return Object.keys(fields).length > 0
    ? { result: { ...result, fields }, modelCalled: false }
    : { result: null, modelCalled: false, completedEmpty: true };
}

/* ── Public entry point ─────────────────────────────────────────────── */

/**
 * Drop-in for `extractFromDocument` that consults the (memory →
 * Firestore → wrapped function) cache pipeline.
 *
 * Returns `null` on the same conditions the wrapped function does
 * (unsupported section, empty text, Gemini failure). Nulls are NOT
 * cached — a future call will retry.
 */
export async function memoisedExtractFromDocument(
  input: MemoisedExtractInput,
): Promise<ExtractFromDocumentResult | null> {
  const { doc, section, siteId, bypassCache, runner, siteState, siteName, abortSignal } = input;
  throwIfAborted(abortSignal);

  const hash = extractionSourceIdentity(doc, [siteId, siteState, siteName]);
  const key = cacheKey(doc.id, section, hash);

  if (!bypassCache) {
    const inMem = memoryCache.get(key);
    if (inMem) {
      stats.hits += 1;
      return checkedSectionCache(inMem, doc, section);
    }
    // Try Firestore (only when siteId provided).
    if (siteId) {
      const persisted = await firestoreRead(siteId, key);
      throwIfAborted(abortSignal);
      if (persisted) {
        memoryCache.set(key, persisted);
        stats.hits += 1;
        return checkedSectionCache(persisted, doc, section);
      }
    }
  }

  stats.misses += 1;
  const fresh = await _extractFromDocument({ doc, section, runner, siteState, siteName, abortSignal });
  throwIfAborted(abortSignal);
  if (fresh) {
    memoryCache.set(key, fresh);
    if (siteId) {
      // CW-2 — enqueue into the per-site batch buffer; the debounced /
      // size-capped flush handles the actual `writeBatch.commit()`.
      // Still fire-and-forget at the caller level — the bootstrap
      // doesn't block on persistence.
      firestoreWrite(siteId, key, fresh);
    }
  }
  return fresh;
}

/**
 * SED D0.3 — discriminated-outcome variant of
 * `memoisedExtractFromDocument`. Returns the SUCCESS result (from the
 * cache or a fresh call) OR the structured `ExtractionRejection` so the
 * bootstrap doc-aware loop can stamp a non-silent `extractionAttempt`
 * with the reason.
 *
 * Cache semantics are IDENTICAL to the success-only wrapper: only the
 * success arm is memoised (memory + Firestore); rejections are NEVER
 * cached — a transient call-failure / out-of-range payload might
 * succeed on a later rebuild, and caching it would lock in the gap
 * (the same "nulls are not cached" invariant, now extended to
 * rejections). The cache key is shared with `memoisedExtractFromDocument`
 * so the two wrappers hit the same memoised success entries.
 */
export async function memoisedExtractWithOutcome(
  input: MemoisedExtractInput,
): Promise<ExtractFromDocumentOutcome> {
  const { doc, section, siteId, bypassCache, runner, siteState, siteName, abortSignal } = input;
  throwIfAborted(abortSignal);

  const hash = extractionSourceIdentity(doc, [siteId, siteState, siteName]);
  const key = cacheKey(doc.id, section, hash);

  if (!bypassCache) {
    const inMem = memoryCache.get(key);
    if (inMem) {
      stats.hits += 1;
      return checkedSectionCache(inMem, doc, section) ?? { rejected: true, reason: 'all-required-missing', detail: 'Cached fields failed current source validation.' };
    }
    if (siteId) {
      const persisted = await firestoreRead(siteId, key);
      throwIfAborted(abortSignal);
      if (persisted) {
        memoryCache.set(key, persisted);
        stats.hits += 1;
        return checkedSectionCache(persisted, doc, section) ?? { rejected: true, reason: 'all-required-missing', detail: 'Cached fields failed current source validation.' };
      }
    }
  }

  stats.misses += 1;
  const fresh = await _extractFromDocumentDetailed({ doc, section, runner, siteState, siteName, abortSignal });
  throwIfAborted(abortSignal);
  if (!isExtractionRejection(fresh)) {
    memoryCache.set(key, fresh);
    if (siteId) firestoreWrite(siteId, key, fresh);
  }
  return fresh;
}

/**
 * Test convenience — preseed the in-memory cache.
 *
 * Used by the perf-regression test to simulate "cache warm" state
 * without actually running the first rebuild.
 */
export function _preseedExtractionCacheForTests(
  doc: SiteDocument,
  section: SedSectionId,
  result: ExtractFromDocumentResult,
  scope: Pick<MemoisedExtractInput, 'siteId' | 'siteState' | 'siteName'> = {},
): void {
  const hash = extractionSourceIdentity(doc, [scope.siteId, scope.siteState, scope.siteName]);
  memoryCache.set(cacheKey(doc.id, section, hash), result);
}

/* ── D7 — requirement-scoped (channel B) cache ───────────────────────
 *
 * Channel B ran UNCACHED: every SED rebuild re-called Gemini for every
 * (doc, requirement) pair, so unlocking tag-match expansion (D1) without
 * this layer would multiply model spend by the tag-match fan-out on
 * every rebuild. Same collection as channel A (`sites/{siteId}/
 * extractionCache/*`), prefix-isolated by the `req::` key shape, so the
 * CW-2 batch buffer, admin/headless read path and rules coverage are
 * all inherited.
 *
 * Key axes: docId, requirementId, contentHash (re-upload busts),
 * schemaFingerprint (schema edit busts), REQUIREMENT_EXTRACTION_CACHE_VERSION
 * (behaviour change busts). Completed empty responses have terminal receipts;
 * transient failures still cannot lock in a gap.
 */

/** Bump when channel-B extraction behaviour changes (floors, sanitizers…). */
// r2 (P1.4) — requirement extraction reads the whole body.
// r3 — P4: requirement extractions read stated attribution.
export const REQUIREMENT_EXTRACTION_CACHE_VERSION = 'r4';

interface CompletedEmptyRequirementReceipt { cacheOutcome: 'completed-empty-requirement-v1' }
type RequirementCacheEntry = ExtractRequirementFieldsResult | CompletedEmptyRequirementReceipt;
const COMPLETED_EMPTY_REQUIREMENT: CompletedEmptyRequirementReceipt = { cacheOutcome: 'completed-empty-requirement-v1' };
function isCompletedEmptyRequirement(value: unknown): value is CompletedEmptyRequirementReceipt {
  return !!value && typeof value === 'object'
    && (value as CompletedEmptyRequirementReceipt).cacheOutcome === COMPLETED_EMPTY_REQUIREMENT.cacheOutcome;
}
const requirementMemoryCache = new Map<string, RequirementCacheEntry>();

/** Compose the channel-B cache key — exported for tests. */
export function requirementCacheKey(
  docId: string,
  requirementId: string,
  contentHash: string,
): string {
  const version = requirementId === 'utility-letter'
    ? `${REQUIREMENT_EXTRACTION_CACHE_VERSION}-electric-scope-1`
    // Preserve existing receipt identities. Newly added source checks run
    // on cached positives too; they do not require expensive model reruns.
    : ['aquifer-study', 'survey', 'parcel-survey', 'species-survey', 'community-feedback-summary'].includes(requirementId)
      ? `${REQUIREMENT_EXTRACTION_CACHE_VERSION}-source-fields-1`
    : REQUIREMENT_EXTRACTION_CACHE_VERSION;
  return `req::${docId}::${requirementId}::${contentHash}::${requirementSchemaFingerprint(requirementId)}::${version}`;
}

export interface MemoisedRequirementExtractInput extends ExtractRequirementFieldsInput {
  /** Optional — when present, enables the Firestore-persistent layer. */
  siteId?: string;
  /** Forces a fresh extraction (Re-extract / forced scans). */
  bypassCache?: boolean;
  abortSignal?: AbortSignal;
  /** Reuse a completed receipt, but never dispatch a new model call. */
  cacheOnly?: boolean;
}

export interface MemoisedRequirementExtractOutcome {
  result: ExtractRequirementFieldsResult | null;
  /**
   * True when this call invoked the model (a real cache miss). The
   * bootstrap's per-pass budget counts THESE, not cache hits and not
   * preflight-ineligible pairs.
   */
  modelCalled: boolean;
  /** A valid completed response established that this source has no eligible fields. */
  completedEmpty?: true;
  /** Eligible source has no completed cache receipt and the caller's budget is exhausted. */
  deferred?: true;
  /** Failed provider calls are never persisted as empty-source cache receipts. */
  failure?: string;
}

export async function memoisedExtractRequirementFields(
  input: MemoisedRequirementExtractInput,
): Promise<MemoisedRequirementExtractOutcome> {
  const { doc, requirementId, siteId, bypassCache, abortSignal } = input;
  throwIfAborted(abortSignal);

  // Ineligible pairs never reach the model — don't spend a miss (or a
  // Firestore read) on them.
  if (!requirementExtractionEligible(doc, requirementId)) {
    return { result: null, modelCalled: false };
  }

  const scope: unknown[] = [siteId, input.origin, input.authoredSchema];
  // Short-document r4 positive entries remain valid. A formerly truncated
  // requirement prompt must be rerun against its complete captured source.
  if (documentBodyText(doc).trim().length > 120_000) scope.push('requirement-full-body-v1');
  const hash = extractionSourceIdentity(doc, scope);
  const key = requirementCacheKey(doc.id, requirementId, hash);

  if (!bypassCache) {
    const inMem = requirementMemoryCache.get(key);
    if (inMem) {
      stats.hits += 1;
      return isCompletedEmptyRequirement(inMem)
        ? { result: null, modelCalled: false, completedEmpty: true }
        : checkedRequirementCache(inMem, doc, requirementId);
    }
    if (siteId) {
      const persisted = await firestoreRead(siteId, key) as unknown as
        RequirementCacheEntry | null;
      throwIfAborted(abortSignal);
      if (persisted && typeof persisted === 'object' && ('fields' in persisted || isCompletedEmptyRequirement(persisted))) {
        requirementMemoryCache.set(key, persisted);
        stats.hits += 1;
        return isCompletedEmptyRequirement(persisted)
          ? { result: null, modelCalled: false, completedEmpty: true }
          : checkedRequirementCache(persisted, doc, requirementId);
      }
    }
  }

  if (input.cacheOnly) return { result: null, modelCalled: false, deferred: true };
  stats.misses += 1;
  let completedEmpty = false;
  let failure: string | undefined;
  const fresh = await _extractRequirementFields({
    doc,
    requirementId,
    runner: input.runner,
    origin: input.origin,
    authoredSchema: input.authoredSchema,
    abortSignal,
    onCompletedEmpty: () => { completedEmpty = true; },
    onFailure: error => { failure = (error instanceof Error ? error.message : String(error)).slice(0, 240); },
  });
  throwIfAborted(abortSignal);
  if (fresh || completedEmpty) {
    const entry = fresh ?? COMPLETED_EMPTY_REQUIREMENT;
    requirementMemoryCache.set(key, entry);
    if (siteId) {
      firestoreWrite(siteId, key, entry as unknown as ExtractFromDocumentResult);
    }
  }
  return { result: fresh, modelCalled: true, ...(completedEmpty && !fresh ? { completedEmpty: true as const } : {}),
    ...(failure ? { failure } : {}) };
}
